import { Button, Form, Input } from "antd";
import Dragger from "antd/es/upload/Dragger";
import dayjs from "dayjs";
import { forEach } from "lodash";
import { useEffect, useState } from "react";
import { toast as toastMessage } from "react-toastify";
import * as XLSX from "xlsx";
import { useAppSelector } from "../../../app/store";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import CustomModal from "../../../components/ui/CustomModal";
import { useCreateVendorMutation } from "../../../features/portal/vendorPayment/vendorPaymentApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { stringToArray } from "../../../utils/array";
import { sliceString } from "../../../utils/string";

type Props = {
  open: boolean;
  handleClose: () => void;
  data?: any;
  isBatchName: boolean;
  // reRender: (val: boolean) => void;
  // uploadedCount: (val: number, total: number) => void;
};

export default function PayrollImportModal({
  open,
  handleClose,
  data: tableLength,
  isBatchName,
}: // reRender,
// uploadedCount,
Props) {
  const [form] = Form.useForm();
  const [fileName, setFileName] = useState("No file chosen");
  const [loading, setLoading] = useState(false);
  const [uploadedData, setUploadedData] = useState<any[]>([]);

  const { user } = useAppSelector((state) => state.auth);

  const [createVendor, { isLoading, isError, error, data: createdData }] =
    useCreateVendorMutation();
  useError(isError, error, "Vendor payment data upload fail");

  const onFinish = async (values: any) => {
    if (!uploadedData?.length) {
      toastMessage.error(
        "Data is empty! please upload vendor payment excel again"
      );
      return;
    }

    if (uploadedData?.filter((item) => item?.amount < 0).length) {
      toastMessage.error("Amount cannot be negative");
      return;
    }

    const updatedData = uploadedData?.map((item: any) => ({
      ...item,
      // disbursement_batch_code: generateBatchCode(tableLength),
      disbursement_batch_name: values.disbursement_batch_name,
    }));

    createVendor(updatedData);
  };

  const handleUploadFile = (file: any) => {
    if (
      file?.name?.endsWith(".xls") ||
      file?.name?.endsWith(".xlsx") ||
      file?.type === "application/vnd.ms-excel" ||
      file?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setLoading(true);
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      const fileNameArr = stringToArray(file?.name, ".");
      setFileName(
        `${sliceString(file?.name, 30, "...")}.${
          fileNameArr?.[fileNameArr?.length - 1]
        }`
      );

      reader.onload = (e) => {
        const data = e?.target?.result;
        const workBook = XLSX.read(data, {
          type: "binary",
          cellDates: true,
          dateNF: "mm/dd/yyyy",
        });
        const sheetName = workBook.SheetNames[0];
        const sheet = workBook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { raw: false });

        // Mapping and structuring data with required fields
        const transformedData = parsedData.map((el: any) => {
          const rawDate =
            el?.["transaction_date"]?.trim() || el?.["Txn. Date"]?.trim() || "";

          // Convert "20.03.2025" -> "2025-03-25"
          const formattedDate = rawDate
            ? dayjs(rawDate, "DD.MM.YYYY").format("YYYY-MM-DD")
            : "";

          return {
            company_id: user?.data?.company_id,
            vendor_id:
              el?.["vendor_id"]?.trim() || el?.["Vendor ID"]?.trim() || "",
            vendor_name:
              el?.["vendor_name"]?.trim() || el?.["Vendor Name"]?.trim() || "",
            wallet_name:
              el?.["wallet_name"]?.trim() ||
              el?.["Wallet Number"]?.trim() ||
              "",
            transaction_id:
              el?.["transaction_id"]?.trim() || el?.["Txn. ID"]?.trim() || "",
            transaction_date: formattedDate,
            amount: el?.["amount"] || el?.["Amount"] || 0,
            narration:
              el?.["narration"]?.trim() || el?.["Narration"]?.trim() || "",
            status: el?.["status"]?.trim() || el?.["Status"]?.trim() || "",
            remarks: el?.["remarks"]?.trim() || el?.["Remarks"]?.trim() || "",
          };
        });

        setUploadedData(transformedData);
        setLoading(false);
      };
    } else {
      setLoading(false);
      toast("error", "Please upload only excel file");
    }
  };

  useEffect(() => {
    if (createdData?.length) {
      forEach(createdData, (item) => {
        if (item?.issuccess) {
          toastMessage.success(item?.message);
          // message.success(item?.message);
        } else {
          // message.error(item?.message);
          toastMessage.error(item?.message);
        }
      });

      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdData]);

  // useEffect(() => {
  //   if (isSuccess) {
  //     toast("success", "Vendor payment upload successfully");
  //     handleClose();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isSuccess]);

  return (
    <>
      <CustomModal
        width={600}
        title="Excel Sheet"
        open={open}
        handleClose={handleClose}
        footer={null}
      >
        <div className="p-6">
          <label className="text-[#3d4a5c] text-sm font-medium leading-normal inline-block mb-2">
            Import Excel File
          </label>
          <Dragger
            name="file"
            action=""
            accept=".xlsx, .xls"
            maxCount={1}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => handleUploadFile(info.file)}
            onDrop={(e) => handleUploadFile(e.dataTransfer.files[0])}
            // excelPostLoading ||
            disabled={loading || isLoading}
            className="import_excel mb-5 excel-upload"
          >
            <Button
              className="flex justify-between items-center disabled:!bg-transparent"
              size="large"
              disabled={loading || isLoading}
              style={{ width: "100%" }}
            >
              <div className="flex items-center gap-2 text-sm font-normal font-roboto text-[#8797ae]">
                <span className="text-accent font-medium text-base leading-7 relative">
                  Choose File
                </span>
                <span className="border-r border-solid border-l-0 border-y-0 border-others-border h-4" />
                {loading ? "Uploading..." : fileName}
              </div>
            </Button>
          </Dragger>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-body-text text-sm leading-normal">
              Download Sample File
            </span>
            <Button
              className="download-button !p-0 !h-fit !border-none !shadow-none !font-semibold"
              size="small"
              icon={<DownloadIcon />}
              type="link"
              href="/Vendor Payment Format.xlsx"
              target="_blank"
              download
            >
              Download
            </Button>
          </div>

          <Form
            className="custom-form mt-2.5"
            layout="vertical"
            requiredMark="optional"
            size="large"
            onFinish={onFinish}
            form={form}
          >
            {/* <Form.Item
              label="Disbursement Batch Code"
              name="disbursement_batch_code"
              rules={[
                {
                  required: true,
                  message: "Please enter the disbursement batch code",
                },
              ]}
            >
              <Input placeholder="Enter disbursement batch code" />
            </Form.Item> */}

            {isBatchName === true && (
              <Form.Item
                label="Disbursement Batch Name"
                name="disbursement_batch_name"
                rules={[
                  {
                    required: true,
                    message: "Please enter the disbursement batch name",
                  },
                ]}
              >
                <Input placeholder="Enter disbursement batch name" />
              </Form.Item>
            )}

            <div className="flex justify-center mt-7">
              <Button
                size="large"
                type="primary"
                className="w-72 !h-12"
                htmlType="submit"
                loading={isLoading}
              >
                Upload
              </Button>
            </div>
          </Form>
        </div>
      </CustomModal>
    </>
  );
}
